
import { Shield, FileCheck, BarChart3, AlertTriangle, Lock, Scale } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const Nist80053Assessment = () => {
  return (
    <LandingPageTemplate
      title="NIST 800-53 Assessment"
      description="Comprehensive NIST 800-53 security controls assessment to evaluate and enhance your organization's security program."
      keywords={[
        "NIST 800-53",
        "security controls",
        "federal compliance",
        "security assessment",
        "regulatory compliance"
      ]}
      canonicalUrl="/cybersecurity/nist-800-53-assessment/"
      schemaType="service"
      schemaData={{
        name: "NIST 800-53 Assessment Services",
        description: "Expert evaluation of your security controls against NIST 800-53",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Federal Standards"
        highlighted="NIST 800-53 Assessment"
        description="Expert evaluation of your security controls against NIST 800-53 requirements for federal information systems and organizations."
        ctaText="Schedule Assessment"
        heroPattern="grid"
      />

      <FeatureSection 
        title="Comprehensive NIST 800-53 Assessment"
        subtitle="KEY FEATURES"
        description="Our assessment provides a thorough evaluation of your security controls across all NIST 800-53 control families."
        features={[
          {
            title: "Control Evaluation",
            description: "Detailed assessment of security controls across all control families.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Gap Analysis",
            description: "Identify gaps between current controls and NIST 800-53 requirements.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation Review",
            description: "Review of security policies, procedures, and control documentation.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk Assessment",
            description: "Evaluation of control effectiveness and residual risk.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Control Selection",
            description: "Tailored control selection based on impact levels.",
            icon: <Scale className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Access Review",
            description: "Assessment of access control implementations.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Assessment Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to evaluating your security controls against NIST 800-53"
        steps={[
          {
            title: "Initial Scoping",
            description: "Define assessment boundaries and system categorization"
          },
          {
            title: "Control Assessment",
            description: "Evaluate implementation of selected security controls"
          },
          {
            title: "Gap Analysis",
            description: "Identify control deficiencies and improvement areas"
          },
          {
            title: "Risk Assessment",
            description: "Evaluate risk impact and likelihood"
          },
          {
            title: "Recommendations",
            description: "Develop detailed recommendations for control enhancement"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our NIST 800-53 Assessment"
        benefits={[
          {
            title: "Federal Expertise",
            description: "Work with certified NIST framework specialists"
          },
          {
            title: "Comprehensive Coverage",
            description: "Full evaluation across all control families"
          },
          {
            title: "Clear Action Plan",
            description: "Actionable recommendations by control family"
          },
          {
            title: "Regulatory Alignment",
            description: "Ensure alignment with federal requirements"
          },
          {
            title: "Risk Reduction",
            description: "Identify and address control weaknesses"
          },
          {
            title: "Future Readiness",
            description: "Prepare for evolving federal requirements"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Assess Your NIST 800-53 Controls?"
        description="Schedule your NIST 800-53 assessment today and strengthen your security controls."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request NIST 800-53 Assessment"
        modalDescription="Tell us about your organization's needs for NIST 800-53 compliance."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "NIST 800-53 Assessment",
          service_interest: "Security Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default Nist80053Assessment;
